import { useEffect, useRef, useState } from "react";
import { Music, VolumeX, Volume2 } from "lucide-react";

/**
 * Background music with smart autoplay strategy:
 *
 * Modern browsers block autoplay with sound until user interaction.
 * Strategy:
 *  1) Mount the audio muted and try to autoplay (almost always succeeds).
 *  2) Listen for first user gesture (click/touch/keydown anywhere on the page).
 *     On first gesture: unmute and continue playing.
 *  3) Always show a floating control (bottom-right) so the user can toggle
 *     volume / pause at any time. Persists state in localStorage.
 */
export default function BackgroundMusic({ src = "/audio/background.mp3", volume = 0.5 }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("bgmusic_muted") === "1";
  });
  const [interacted, setInteracted] = useState(false);

  // Initial autoplay attempt (muted to bypass browser restrictions)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
    audio.muted = true; // start muted so autoplay works everywhere
    audio.loop = true;
    const tryPlay = async () => {
      try {
        await audio.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    };
    tryPlay();
  }, [volume]);

  // On first user interaction, unmute (unless user explicitly muted)
  useEffect(() => {
    if (interacted) return;
    const audio = audioRef.current;
    if (!audio) return;

    const onFirstInteraction = () => {
      setInteracted(true);
      if (!muted) {
        audio.muted = false;
      }
      // Make sure it's playing
      audio.play().catch(() => {});
    };
    const opts = { once: true, passive: true };
    window.addEventListener("pointerdown", onFirstInteraction, opts);
    window.addEventListener("touchstart", onFirstInteraction, opts);
    window.addEventListener("keydown", onFirstInteraction, opts);
    return () => {
      window.removeEventListener("pointerdown", onFirstInteraction);
      window.removeEventListener("touchstart", onFirstInteraction);
      window.removeEventListener("keydown", onFirstInteraction);
    };
  }, [interacted, muted]);

  // Reflect mute state to <audio>
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Only honor user mute AFTER first interaction (browsers force muted before)
    if (interacted) audio.muted = muted;
    if (typeof window !== "undefined") {
      localStorage.setItem("bgmusic_muted", muted ? "1" : "0");
    }
  }, [muted, interacted]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.muted = false;
      audio.play().catch(() => {});
      setMuted(false);
      setPlaying(true);
      setInteracted(true);
    } else {
      // Toggle mute (more useful than pause for background music)
      setMuted((m) => !m);
    }
  };

  // Don't show "click to enable" hint forever — only briefly until user interacts.
  const showHint = !interacted && playing;

  return (
    <>
      <audio ref={audioRef} src={src} preload="auto" />
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col items-end gap-2">
        {showHint && (
          <button
            onClick={() => {
              setInteracted(true);
              const audio = audioRef.current;
              if (audio) {
                audio.muted = false;
                audio.play().catch(() => {});
              }
              setMuted(false);
            }}
            data-testid="bgmusic-enable-hint"
            className="px-3 py-2 rounded-full bg-yellow-400 text-black text-xs font-bold shadow-2xl animate-pulse flex items-center gap-1.5 border-2 border-yellow-300"
          >
            <Volume2 className="w-3.5 h-3.5" />
            Tap para sonido
          </button>
        )}
        <button
          onClick={toggle}
          aria-label={muted ? "Activar música" : "Silenciar música"}
          data-testid="bgmusic-toggle"
          className={`w-12 h-12 rounded-full backdrop-blur-md border-2 transition-all shadow-xl flex items-center justify-center ${
            muted
              ? "bg-black/60 border-white/30 text-white/70 hover:border-white/60"
              : "bg-yellow-400/20 border-yellow-400/70 text-yellow-300 hover:bg-yellow-400/30"
          }`}
        >
          {muted ? (
            <VolumeX className="w-5 h-5" />
          ) : (
            <Music className="w-5 h-5 animate-pulse" />
          )}
        </button>
      </div>
    </>
  );
}
